/**
 * React Auth Service for standalone web authentication.
 *
 * Token Storage Strategy:
 *   Internal SSO: cookies ({cookieEnv}ACCESS_TOKEN, {cookieEnv}REFRESH_TOKEN)
 *   External OHID: sessionStorage (accessToken, refreshToken, idToken)
 *
 * User Data Storage:
 *   sessionStorage: currentUser (array), loggedUser (object)
 *   localStorage (internal only): UHC_CURRENT_USER (cross-tab sync)
 *   localStorage (external only): UHC_ID_TOKEN, UHC_UUID (page refresh recovery)
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- Legacy SSO/OHID payloads have heterogeneous runtime shapes. */

import { AuthConfig } from './authConfig';
import { getInitializeState } from '@src/CommonComponents/ChangeProviderComponent/constant';

// ─── Cookie Helpers ──────────────────────────────────────────

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, domain: string): void {
  // Match Angular's CookieService behavior but avoid invalid domain=localhost cookies.
  const isSecure = window.location.protocol === 'https:';
  const securePart = isSecure ? ';secure' : '';
  const isLocalHostDomain = !domain || domain === 'localhost' || domain === '127.0.0.1';
  const domainPart = isLocalHostDomain ? '' : `;domain=${domain}`;
  document.cookie = `${name}=${encodeURIComponent(
    value
  )};path=/${domainPart}${securePart};max-age=86400`;
}

function deleteCookie(name: string, domain: string): void {
  const isLocalHostDomain = !domain || domain === 'localhost' || domain === '127.0.0.1';
  const domainPart = isLocalHostDomain ? '' : `;domain=${domain}`;
  document.cookie = `${name}=;path=/${domainPart};expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

// ─── Token Read ──────────────────────────────────────────────

export function getAccessToken(config: AuthConfig): string | null {
  if (config.isInternal) {
    return (
      getCookie(`${config.cookieEnvironment}ACCESS_TOKEN`) ||
      sessionStorage.getItem('INTERNAL_ACCESS_TOKEN')
    );
  }
  return sessionStorage.getItem('accessToken');
}

export function getRefreshToken(config: AuthConfig): string | null {
  if (config.isInternal) {
    return (
      getCookie(`${config.cookieEnvironment}REFRESH_TOKEN`) ||
      sessionStorage.getItem('INTERNAL_REFRESH_TOKEN')
    );
  }
  return sessionStorage.getItem('refreshToken');
}

export function getIdToken(config: AuthConfig): string | null {
  if (config.isInternal) {
    return (
      getCookie(`${config.cookieEnvironment}ID_TOKEN`) ||
      sessionStorage.getItem('INTERNAL_ID_TOKEN')
    );
  }
  // External: sessionStorage first, then localStorage fallback for page refresh
  let token = sessionStorage.getItem('idToken');
  if (token) return token;
  token = localStorage.getItem('UHC_ID_TOKEN');
  if (token) {
    sessionStorage.setItem('idToken', token); // restore
    return token;
  }
  return null;
}

// ─── Token Write ─────────────────────────────────────────────

function storeTokensInternal(
  accessToken: string,
  refreshToken: string | null,
  idToken: string | null,
  config: AuthConfig
): void {
  setCookie(`${config.cookieEnvironment}ACCESS_TOKEN`, accessToken, config.domain);
  if (refreshToken)
    setCookie(`${config.cookieEnvironment}REFRESH_TOKEN`, refreshToken, config.domain);
  if (idToken) setCookie(`${config.cookieEnvironment}ID_TOKEN`, idToken, config.domain);

  // Fallback for localhost where domain cookies can be unreliable in some browsers/dev setups.
  sessionStorage.setItem('INTERNAL_ACCESS_TOKEN', accessToken);
  if (refreshToken) sessionStorage.setItem('INTERNAL_REFRESH_TOKEN', refreshToken);
  if (idToken) sessionStorage.setItem('INTERNAL_ID_TOKEN', idToken);
}

function storeTokensExternal(
  accessToken: string,
  refreshToken: string | null,
  idToken: string | null
): void {
  sessionStorage.setItem('accessToken', accessToken);
  if (refreshToken) sessionStorage.setItem('refreshToken', refreshToken);
  if (idToken) {
    sessionStorage.setItem('idToken', idToken);
    // Persist to localStorage for page refresh recovery (mirrors Angular storageService.saveIdToken)
    localStorage.setItem('UHC_ID_TOKEN', idToken);
  }
}

export function storeTokens(
  accessToken: string,
  refreshToken: string | null,
  idToken: string | null,
  config: AuthConfig
): void {
  if (config.isInternal) {
    storeTokensInternal(accessToken, refreshToken, idToken, config);
  } else {
    storeTokensExternal(accessToken, refreshToken, idToken);
  }
}

// ─── Token Expiry Check ──────────────────────────────────────

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function getTokenExpiry(token: string): Date | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return new Date(payload.exp * 1000);
  } catch {
    return null;
  }
}

// ─── MSAD Groups Extraction (mirrors Angular JarvisdashboardsConfig check) ──

function extractAndSaveMsadGroups(idToken: string | null): void {
  if (!idToken) return;
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    const groups = payload?.groups;
    if (groups && groups.length > 0) {
      sessionStorage.setItem('msad_groups', btoa(JSON.stringify(groups)));
    }
  } catch {
    // Token may not be a valid JWT (e.g., opaque token for external users)
  }
}

// ─── MsId Encryption (mirrors Angular EncryptMsidService) ───

function encryptAndSaveMsId(msId: string | null): void {
  if (!msId) return;
  // Simple base64 encoding for MsId storage (matches Angular's encryptMsidService.encryptMsId)
  sessionStorage.setItem('MsId', btoa(msId.toLowerCase()));
}

// ─── User Storage (sessionStorage — shared with Angular) ─────
export function getCurrentUser(): any | null {
  // sessionStorage first (primary), localStorage fallback for internal users
  const raw = sessionStorage.getItem('currentUser');
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const lsRaw = localStorage.getItem('currentUser') || localStorage.getItem('UHC_CURRENT_USER');
  if (lsRaw) {
    try {
      return JSON.parse(lsRaw);
    } catch {
      return null;
    }
  }
  return null;
}

export function getSessionUser(): any | null {
  const raw = sessionStorage.getItem('currentUser');
  if (raw) {
    try {
      return true;
    } catch {
      return false;
    }
  } else {
    return false;
  }
}

export function saveCurrentUser(user: any, config?: AuthConfig): void {
  const userArray = Array.isArray(user) ? user : [user];
  sessionStorage.setItem('currentUser', JSON.stringify(userArray));
  // Internal users: save to localStorage for cross-tab sync (mirrors Angular storageService.saveCurrentUser)
  if (config?.isInternal) {
    localStorage.setItem('UHC_CURRENT_USER', JSON.stringify(userArray));
  }
}

export function getLoggedUser(): any | null {
  const raw = sessionStorage.getItem('loggedUser');
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const lsRaw = localStorage.getItem('loggedUser');
  if (lsRaw) {
    try {
      return JSON.parse(lsRaw);
    } catch {
      return null;
    }
  }
  return null;
}

export function saveLoggedUser(user: any): void {
  const serialized = JSON.stringify(user);
  sessionStorage.setItem('loggedUser', serialized);
  // Also persist to localStorage so new tabs can restore loggedUser (sessionStorage is tab-scoped)
  localStorage.setItem('loggedUser', serialized);
}

async function getCookiesResponse(): Promise<any> {
  const response = await fetch('/cookies', {
    credentials: 'include',
  });

  if (!response.ok) {
    const error: any = new Error(`Cookies fetch failed: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

function getCookieValueCaseInsensitive(
  cookies: Record<string, string> | null | undefined,
  candidateNames: string[]
): string | null {
  if (!cookies) {
    return null;
  }

  const cookieKeys = Object.keys(cookies);
  const lowerKeyByLowerName = new Map(cookieKeys.map((key) => [key.toLowerCase(), key]));

  for (const name of candidateNames) {
    const actualKey = lowerKeyByLowerName.get(name.toLowerCase());
    const value = actualKey ? cookies[actualKey] : undefined;
    if (value != null && String(value).trim() !== '') {
      return value;
    }
  }

  return null;
}

function saveUuid(uuid: string): void {
  sessionStorage.setItem('Uuid', uuid);
  sessionStorage.setItem('uuid', uuid);
  // External: persist to localStorage for page refresh recovery
  localStorage.setItem('UHC_UUID', uuid);
}

function getUuid(): string | null {
  return (
    sessionStorage.getItem('Uuid') ||
    sessionStorage.getItem('uuid') ||
    localStorage.getItem('UHC_UUID') ||
    null
  );
}

function setMpinTinDetails(tins: any[]): void {
  sessionStorage.setItem('mpinTinData', JSON.stringify(tins));
}

function getDefaultExternalSessionState(): Record<string, any> {
  try {
    const state = getInitializeState();
    return {
      ...state,
      market: state.market ?? '',
      Vendor: state.Vendor ?? [],
      uhc: state.uhc ?? {},
      kop: state.kop ?? {},
    };
  } catch {
    return { uhc: {}, kop: {} };
  }
}

function hydrateExternalSessionState(tins: any[], selectedTin: any): void {
  const stateRaw = sessionStorage.getItem('state');
  const defaultState = getDefaultExternalSessionState();
  const existingState = stateRaw ? JSON.parse(stateRaw) : {};
  const stateObj = {
    ...defaultState,
    ...existingState,
    uhc: {
      ...(defaultState.uhc || {}),
      ...(existingState.uhc || {}),
    },
    kop: {
      ...(defaultState.kop || {}),
      ...(existingState.kop || {}),
    },
  };

  stateObj.uhc = stateObj.uhc || {};
  stateObj.uhc.Tins = tins;
  stateObj.uhc.selectedCorporateTin = selectedTin;
  stateObj.Tins = tins;
  stateObj.selectedCorporateTin = selectedTin;
  stateObj.tinsInfo = tins;

  sessionStorage.setItem('state', JSON.stringify(stateObj));
}

export function getMpinTinDetails(): any[] {
  try {
    const stored = sessionStorage.getItem('mpinTinData');
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : parsed?.Tins || [];
    }
  } catch {
    /* ignore */
  }
  return [];
}

// ─── Session Count (multi-tab tracking — same cookie as Angular) ─

function getSessionCount(config: AuthConfig): number {
  const val = getCookie(`${config.cookieEnvironment}SESSION_COUNT`);
  return val ? parseInt(val, 10) : 0;
}

function setSessionCount(count: number, config: AuthConfig): void {
  setCookie(`${config.cookieEnvironment}SESSION_COUNT`, String(count), config.domain);
}

export function incrementSessionCount(config: AuthConfig): void {
  const reload = sessionStorage.getItem('reload');
  if (reload === 'true') return; // Don't double-increment on page reload
  setSessionCount(getSessionCount(config) + 1, config);
}

export function decrementSessionCount(config: AuthConfig): void {
  const current = getSessionCount(config);
  if (current > 0) setSessionCount(current - 1, config);
}

// ─── External Session Values ─

function setExternalSessionDefaults(): void {
  sessionStorage.setItem('logout', 'false');
  sessionStorage.setItem('simulationMode', 'false');
  if (!sessionStorage.getItem('multiSelectedTins')) sessionStorage.setItem('multiSelectedTins', '');
  sessionStorage.setItem('journeyId', '2');
  sessionStorage.setItem('GeoCriteria', 'null');
  if (!sessionStorage.getItem('state')) {
    sessionStorage.setItem('state', JSON.stringify(getDefaultExternalSessionState()));
  }
}

// ─── 1. Initiate Login (redirect to IdP) ─────────────────────

/**
 * Initiates login by requesting the IdP login URL from backend.
 *
 * Internal: POST {ssoApiProxyUrl}api/auth/login?redirectUri={origin}/
 * External: POST {ohidApiProxyUrl}api/auth/ohid/login?redirectUri={redirectUri}
 */
export async function initiateLogin(config: AuthConfig): Promise<void> {
  const baseUrl = config.isInternal ? config.ssoApiProxyUrl : config.ohidApiProxyUrl;
  const endpoint = config.loginUrl;
  const redirectUri = config.isInternal ? `${location.origin}/` : config.redirectUri;

  const params = new URLSearchParams({ redirectUri });

  // Build headers matching Angular's token-interceptor EXACTLY
  // Angular interceptor adds ALL these headers to EVERY request
  // For login URL: interceptor SKIPS Authorization (url contains 'login')
  // but STILL adds UserType, Accept, X-Upstream-Env, x-idp, Content-Type
  const headers: Record<string, string> = {
    Accept: '*/*',
    'Content-Type': 'application/json',
    'x-idp': config.isInternal ? 'azuread' : 'onehealthcareid',
    'X-Upstream-Env': config.upstreamEnv,
    UserType: config.isInternal ? 'Internal' : 'External',
  };

  // No Authorization header for login URL (Angular interceptor skips urls containing 'login')

  // Angular interceptor: withCredentials only when XSRFToggle && POST && xsrf token
  const credentials: RequestCredentials = config.XSRFToggle ? 'include' : 'same-origin';

  const response = await fetch(`${baseUrl}${endpoint}?${params.toString()}`, {
    method: 'POST',
    credentials,
    headers,
  });

  const data = await response.json();
  if (data?.loginUrl) {
    sessionStorage.setItem('OHID_LOGIN_INITIATED', 'true');
    window.location.replace(data.loginUrl);
  }
}

// ─── 2. Exchange Auth Code for Tokens ────────────────────────

export interface TokenExchangeResult {
  accessToken: string;
  refreshToken: string | null;
  idToken: string | null;
  expiresIn: number | null;
  uuid: string | null;
  emulatedUuid: string | null;
  rawUser: any;
}

/**
 * Exchanges auth code for tokens.
 *
 * Behavior:
 *   1. toggleNewLoginApiChanges: true → POST, false → GET
 *   2. Adds auth headers required by backend
 *   3. POST + XSRFToggle → adds X-XSRF-TOKEN header + withCredentials
 *   4. OHID callback uses application/x-www-form-urlencoded, SSO uses application/json
 */
export async function exchangeAuthCode(
  code: string,
  config: AuthConfig
): Promise<TokenExchangeResult> {
  const baseUrl = config.isInternal ? config.ssoApiProxyUrl : config.ohidApiProxyUrl;
  const endpoint = config.callbackUrl;
  const redirectUri = config.isInternal ? `${location.origin}/` : config.redirectUri;

  // Angular uses HttpParams → query params (not body)
  const params = new URLSearchParams({ code, redirectUri });

  // Determine HTTP method: Angular uses POST when toggleNewLoginApiChanges, GET otherwise
  const method = config.toggleNewLoginApiChanges ? 'POST' : 'GET';

  // Content-Type: Angular's getContentType() returns 'application/x-www-form-urlencoded'
  // for OHID callback (url contains 'api/auth/ohid/callback'), 'application/json' for SSO callback
  const isOhidCallback = endpoint.includes('ohid/callback');
  const contentType = isOhidCallback ? 'application/x-www-form-urlencoded' : 'application/json';

  // Build headers matching Angular's token-interceptor EXACTLY
  // Angular interceptor adds ALL these headers to EVERY request (including callback)
  const headers: Record<string, string> = {
    Accept: '*/*',
    'Content-Type': contentType,
    'x-idp': config.isInternal ? 'azuread' : 'onehealthcareid',
    'X-Upstream-Env': config.upstreamEnv,
    UserType: config.isInternal ? 'Internal' : 'External',
  };

  // Angular interceptor skips Authorization for URLs containing 'login' or 'logout'
  // Callback URL ('api/auth/callback') does NOT contain 'login'/'logout'
  // So Authorization IS added if a token exists
  const existingAccessToken = getAccessToken(config);
  if (existingAccessToken && !endpoint.includes('login') && !endpoint.includes('logout')) {
    headers['Authorization'] = `Bearer ${existingAccessToken}`;
  }

  // Angular's interceptor: if POST && XSRFToggle && xsrfToken exists → add X-XSRF-TOKEN
  if (method === 'POST' && config.XSRFToggle) {
    const xsrfToken = getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      headers['X-XSRF-TOKEN'] = xsrfToken;
    }
  }

  // Angular interceptor: withCredentials only when XSRFToggle && POST && xsrf token
  const credentials: RequestCredentials = config.isInternal
    ? config.XSRFToggle && method === 'POST'
      ? 'include'
      : 'same-origin'
    : 'include';

  console.log('[React Auth] exchangeAuthCode URL:', `${baseUrl}${endpoint}?${params.toString()}`);
  console.log(
    '[React Auth] exchangeAuthCode method:',
    method,
    'credentials:',
    credentials,
    'headers:',
    Object.keys(headers)
  );

  const response = await fetch(`${baseUrl}${endpoint}?${params.toString()}`, {
    method,
    credentials,
    headers,
  });

  if (!response.ok) {
    const err: any = new Error(`Token exchange failed: ${response.status}`);
    err.status = response.status;
    throw err;
  }

  if (config.isInternal) {
    return parseInternalResponse(response, config);
  } else {
    return parseExternalResponse(response);
  }
}

/**
 * Parse internal SSO callback response — EXACT 1:1 copy of Angular's ssoLogIn() parsing.
 *
 * Angular code:
 *   const keys = response.headers.get('cookie');
 *   const cookies = keys.split(';');
 *   const token = cookies.filter(this.checkToken); // items containing "token"
 *   tokenArrays[0].value = token[0].split('=')[1].trim().replace(/^"|"$/g, ''); // accessToken
 *   tokenArrays[1].value = token[1].split('=')[1].trim().replace(/^"|"$/g, ''); // idToken
 */
async function parseInternalResponse(
  response: Response,
  config: AuthConfig
): Promise<TokenExchangeResult> {
  const cookieEnv = config.cookieEnvironment;

  let accessToken = '';
  let idToken: string | null = null;

  // Log all visible response headers
  console.log('[React Auth] parseInternalResponse — all response headers:');
  response.headers.forEach((value: string, key: string) => {
    console.log(`  [header] ${key}: ${value.substring(0, 120)}`);
  });

  // ─── EXACT Angular approach: response.headers.get('cookie') ──
  const keys = response.headers.get('cookie');
  console.log(
    '[React Auth] response.headers.get("cookie"):',
    keys ? keys.substring(0, 200) : 'NULL'
  );

  if (keys) {
    // Exact Angular: const cookies = keys.split(';');
    const cookies = keys.split(';');
    // Exact Angular: const token = cookies.filter(this.checkToken);
    // checkToken: return cookieParam.toLowerCase().indexOf('token') !== -1;
    const tokenItems = cookies.filter(
      (cookieParam: string) => cookieParam.toLowerCase().indexOf('token') !== -1
    );
    console.log(
      '[React Auth] token items after filter:',
      tokenItems.length,
      tokenItems.map((t: string) => t.split('=')[0]?.trim())
    );

    // Exact Angular: tokenArrays = [{key:'accessToken'},{key:'idToken'}]
    // tokenArrays[0].value = token[0].split('=')[1]
    // tokenArrays[1].value = token[1].split('=')[1]
    if (tokenItems.length >= 1) {
      let extracted = tokenItems[0].split('=')[1];
      extracted = extracted ? extracted.trim() : '';
      if (extracted.startsWith('"') && extracted.endsWith('"')) extracted = extracted.slice(1, -1);
      accessToken = extracted;
      console.log(
        '[React Auth] accessToken from cookie header:',
        accessToken ? `${accessToken.substring(0, 30)}... (${accessToken.length} chars)` : 'EMPTY'
      );
    }
    if (tokenItems.length >= 2) {
      let extracted = tokenItems[1].split('=')[1];
      extracted = extracted ? extracted.trim() : '';
      if (extracted.startsWith('"') && extracted.endsWith('"')) extracted = extracted.slice(1, -1);
      idToken = extracted;
      console.log(
        '[React Auth] idToken from cookie header:',
        idToken ? `${idToken.substring(0, 30)}... (${idToken.length} chars)` : 'EMPTY'
      );
    }
  }

  // ─── Fallback: read from document.cookie (Set-Cookie already applied by browser) ──
  if (!accessToken) {
    console.log('[React Auth] cookie header empty/missing — trying document.cookie...');
    accessToken = getCookie(`${cookieEnv}ACCESS_TOKEN`) || '';
    console.log(
      '[React Auth] accessToken from document.cookie:',
      accessToken ? `found (${accessToken.length} chars)` : 'NOT FOUND'
    );
  }
  if (!idToken) {
    idToken = getCookie(`${cookieEnv}ID_TOKEN`) || null;
    console.log(
      '[React Auth] idToken from document.cookie:',
      idToken ? `found (${idToken.length} chars)` : 'NOT FOUND'
    );
  }

  // ─── Fallback: try response body ──
  if (!accessToken) {
    console.log('[React Auth] still no token — trying response body...');
    try {
      const body = await response.clone().json();
      console.log('[React Auth] response body keys:', Object.keys(body || {}));
      if (body?.access_token || body?.accessToken) {
        accessToken = body.access_token || body.accessToken;
        idToken = body.id_token || body.idToken || idToken;
      }
    } catch {
      console.log('[React Auth] response body is not JSON');
    }
  }

  console.log(
    '[React Auth] parseInternalResponse FINAL — accessToken:',
    accessToken ? `YES (${accessToken.length})` : 'EMPTY',
    'idToken:',
    idToken ? `YES (${idToken.length})` : 'NULL'
  );

  return {
    accessToken,
    refreshToken: null,
    idToken,
    expiresIn: null,
    uuid: null,
    emulatedUuid: null,
    rawUser: null,
  };
}

async function parseExternalResponse(response: Response): Promise<TokenExchangeResult> {
  const body = await response.json();
  const idToken = body?.id_token || body?.idToken || null;

  // Extract user data from ID token (contains given_name, family_name, email, preferred_username)
  let idTokenPayload: any = null;
  if (idToken) {
    try {
      idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
    } catch {
      // Invalid JWT, will fall back to body fields
    }
  }

  return {
    accessToken: body?.access_token || body?.accessToken || body?.token || '',
    refreshToken: body?.refresh_token || body?.refreshToken || null,
    idToken,
    expiresIn: body?.expires_in || null,
    uuid: body?.uuid || body?.Uuid || null,
    emulatedUuid: body?.emulatedUUID || body?.emulatedUuid || body?.emulatedInsightsUuid || null,
    rawUser: {
      // Prefer ID token data over response body
      FirstName: idTokenPayload?.given_name || body?.givenName || body?.FirstName || null,
      LastName: idTokenPayload?.family_name || body?.familyName || body?.LastName || null,
      EmailId: idTokenPayload?.email || body?.email || body?.EmailId || null,
      OptumId: idTokenPayload?.preferred_username || body?.userName || body?.OptumId || null,
      MsId: null,
    },
  };
}

// ─── 3. Fetch User Details (Internal SSO) ────────────────────

/**
 * Mirrors Angular's SSOLoginService.getSsoUserDetails + token-interceptor headers.
 *
 * Angular uses getLoginURL() to pick the base URL:
 *   - XSRFToggle=true  → postLoginProxyUrl  (e.g. '/uhci/login/dev/')
 *   - XSRFToggle=false → ssoApiProxyUrl     (e.g. 'https://uhci-ui-internal-dev.optum.com/')
 *
 * Angular's token-interceptor adds headers:
 *   Authorization: Bearer {accessToken}
 *   x-idp: azuread
 *   X-Upstream-Env: {upstreamEnv}
 *   Accept: *\/*
 *   UserType: {userType}
 */
export async function fetchUserDetails(idToken: string, config: AuthConfig): Promise<any> {
  // Mirror Angular getLoginURL() — use postLoginProxyUrl when XSRFToggle is true
  const baseUrl =
    config.XSRFToggle && config.postLoginProxyUrl
      ? config.postLoginProxyUrl
      : config.ssoApiProxyUrl;
  const endpoint = config.fetchUserDetailsUrl;
  const params = new URLSearchParams({ idToken });

  // Read the access token that was just stored (mirrors Angular interceptor reading from cookie)
  const accessToken = getAccessToken(config);

  // Read loggedUser for UserId header (mirrors Angular interceptor)
  const loggedUser = getLoggedUser();
  const userId = loggedUser?.MsId || '';

  // Build headers matching Angular's token-interceptor.attachSsoToken() EXACTLY
  // Angular adds ALL these headers to EVERY internal request
  const headers: Record<string, string> = {
    Accept: '*/*',
    'Content-Type': 'application/json',
    'x-idp': 'azuread',
    'X-Upstream-Env': config.upstreamEnv,
    UserType: config.isInternal ? 'Internal' : 'External',
  };

  // UserId header — Angular adds if loggedUser.MsId exists
  if (userId) {
    headers['UserId'] = userId;
  }

  // Authorization header — Angular interceptor: special handling for SsoFetchUserDetails
  // (adds Bearer even when currentUser doesn't exist yet, as long as tokenToAppend is set)
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // XSRF for POST (mirrors Angular interceptor)
  if (config.XSRFToggle) {
    const xsrfToken = getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      headers['X-XSRF-TOKEN'] = xsrfToken;
    }
  }

  // Angular interceptor: withCredentials only when XSRFToggle && POST && xsrf token
  const credentials: RequestCredentials = config.XSRFToggle ? 'include' : 'same-origin';

  console.log('[React Auth] fetchUserDetails URL:', `${baseUrl}${endpoint}?${params.toString()}`);
  console.log(
    '[React Auth] fetchUserDetails headers:',
    Object.keys(headers),
    'credentials:',
    credentials
  );

  const response = await fetch(`${baseUrl}${endpoint}?${params.toString()}`, {
    method: 'POST',
    credentials,
    headers,
  });

  if (!response.ok) {
    const err: any = new Error(`Fetch user details failed: ${response.status}`);
    err.status = response.status;
    throw err;
  }

  return response.json();
}

// ─── 4. Fetch External Provider Data (OHID V3 API) ──────────

/**
 * Mirrors Angular's authenticationService.getOhidV3ProviderData()
 * POST {bulkTinRUrl}/provider-org-jobs/fetch-associated-providers
 */
export async function fetchExternalProviderData(
  accessToken: string,
  uuid: string | null,
  emulatedUuid: string | null,
  _idToken: string | null,
  config: AuthConfig
): Promise<any> {
  const url = `${config.bulkTinRUrl}provider-org-jobs/fetch-associated-providers`;
  let finalUuid = uuid;

  try {
    const cookieResponse = await getCookiesResponse();
    let emulatedUuidFromCookie: string | null = null;

    if (cookieResponse?.cookies) {
      emulatedUuidFromCookie = getCookieValueCaseInsensitive(cookieResponse.cookies, [
        'emulatedInsightsUuid',
        'emulatedUuid',
      ]);
    }

    finalUuid = emulatedUuidFromCookie !== null ? emulatedUuidFromCookie : uuid;
  } catch {
    finalUuid = emulatedUuid !== null && emulatedUuid !== undefined ? emulatedUuid : uuid;
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'x-idp': 'onehealthcareid',
    'X-Upstream-Env': config.upstreamEnv,
    Accept: '*/*',
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ uuid: finalUuid }),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Fetch provider data failed: ${response.status}`);
  }

  return response.json();
}

type EmulatedUserDetails = {
  FirstName: string;
  LastName: string;
  EmailId: string;
};

const normalizeOptionalString = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

export function normalizeEmulatedUserDetails(value: unknown): EmulatedUserDetails | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const details = value as Record<string, unknown>;
  const normalizedDetails = {
    FirstName: normalizeOptionalString(details.firstName),
    LastName: normalizeOptionalString(details.lastName),
    EmailId: normalizeOptionalString(details.email),
  };

  return normalizedDetails.FirstName || normalizedDetails.LastName ? normalizedDetails : null;
}

// ─── 4b. Transform V3 Response (mirrors Angular external.service.CheckExternalOHID) ──

/**
 * Transforms V3 API response into the app's internal format:
 *   - Builds Providers array from flat V3 data
 *   - Builds Tins array from Providers
 *   - Builds complete currentUser object
 */
function transformV3Response(
  apiResponse: any,
  accessToken: string,
  refreshToken: string | null,
  idToken: string | null,
  uuid: string | null,
  _config: AuthConfig
): any {
  const dataArray = apiResponse?.Data;
  if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
    throw new Error(apiResponse?.Message || 'Provider data is empty or invalid');
  }

  const providers = dataArray
    .filter((p: any) => p && typeof p === 'object')
    .map((provider: any) => ({
      Id: provider.id,
      ProviderKey: provider.id,
      ProviderSystem: provider.corpMpin,
      HealthCareOrganizationName:
        `${provider.FirstName || ''} ${provider.LastName || ''}`.trim() ||
        `Provider ${provider.corpMpin}`,
      UHCProviderId: provider.uhcProviderId,
      corpMpin: provider.corpMpin,
      registeredOrg: provider.registeredOrg,
      updateDocDeliverySettings: provider.updateDocDeliverySettings,
      paa: provider.paa,
      ia: provider.ia,
    }));

  if (providers.length === 0) {
    throw new Error('No valid providers found in V3 response');
  }

  // Build Tins from Providers (mirrors Angular external.service non-TIN-level path)
  const tinsArray = providers.map((p: any) => ({
    name: p.HealthCareOrganizationName || p.ProviderSystem || p.corpMpin || '',
    value: p.ProviderKey || p.Id || p.corpMpin || '',
    disable: false,
  }));

  const selectedTin = tinsArray.length > 0 ? tinsArray[0] : null;
  const healthCareOrgName = selectedTin?.name || providers[0]?.HealthCareOrganizationName || '';

  const resolvedUuid = uuid || getUuid() || '';

  // Extract UserPersonas from V3 response or use default
  let userPersonas = dataArray[0]?.UserPersonas || [
    { UserRole: 'External', UserType: 'External', Journey: [{ JourneyId: 2 }] },
  ];
  if (!userPersonas.length || !userPersonas[0]?.Journey?.length) {
    userPersonas = [{ UserRole: 'External', UserType: 'External', Journey: [{ JourneyId: 2 }] }];
  }

  // Build complete currentUser object (matches Angular's external.service exactly)
  const currentUserData = {
    AccessToken: accessToken || '',
    RefreshToken: refreshToken || sessionStorage.getItem('refreshToken') || '',
    IdToken: idToken || '',
    FirstName: dataArray[0]?.givenName || dataArray[0]?.FirstName || '',
    LastName: dataArray[0]?.familyName || dataArray[0]?.LastName || '',
    EmailId: dataArray[0]?.email || dataArray[0]?.EmailId || '',
    OptumId: dataArray[0]?.userName || dataArray[0]?.OptumId || '',
    MsId: null,
    Uuid: resolvedUuid,
    uuid: resolvedUuid,
    Providers: providers,
    PilotExperience: null,
    Tins: tinsArray,
    selectedTin,
    HealthCareOrganizationName: healthCareOrgName,
    NewUser: false,
    UserPersonas: userPersonas,
  };

  return {
    currentUserData,
    tinsArray,
    selectedTin,
    providers,
    dataArray,
    emulatedUserDetails: normalizeEmulatedUserDetails(apiResponse?.emulatedUserDetails),
    loggedUserData: {
      FirstName: currentUserData.FirstName,
      LastName: currentUserData.LastName,
      EmailId: currentUserData.EmailId,
      OptumId: currentUserData.OptumId,
      MsId: null,
    },
  };
}

// ─── 4c. TIN Level Job Submission & Polling (mirrors Angular external.service) ──

async function submitTinJob(
  dataArray: any[],
  uuid: string | null,
  accessToken: string,
  config: AuthConfig
): Promise<any> {
  const jobPayload = {
    uuid,
    providerOrg: dataArray.map((provider: any) => ({
      FirstName: provider.FirstName || '',
      LastName: provider.LastName || '',
      RegisteredOrg: provider.registeredOrg || false,
      UpdateDocDeliverySettings: provider.updateDocDeliverySettings || false,
      Paa: provider.paa || false,
      Ia: provider.ia || false,
      id: provider.id,
      corpMpin: provider.corpMpin,
      uhcProviderId: provider.uhcProviderId,
    })),
  };

  const externalTinsJOB =
    (config as any).externalTinsJOB || 'provider-org-jobs/submit-external-tins-job';
  const url = `${config.bulkTinRUrl}${externalTinsJOB}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
      'X-Upstream-Env': config.upstreamEnv,
      'x-idp': 'onehealthcareid',
    },
    body: JSON.stringify(jobPayload),
    credentials: 'include',
  });

  if (!response.ok) throw new Error('Job submission failed');
  return response.json();
}

async function pollTinJobResult(
  jobId: string,
  accessToken: string,
  config: AuthConfig
): Promise<any> {
  const url = `${config.bulkTinRUrl}contract-exemption-jobs/result/${jobId}`;

  return new Promise((resolve, reject) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: '*/*',
            'X-Upstream-Env': config.upstreamEnv,
            'x-idp': 'onehealthcareid',
          },
          credentials: 'include',
        });
        const data = await response.json();
        if (data?.Data?.JobStatus === 'COMPLETED' || data?.Data?.result) {
          clearInterval(pollInterval);
          resolve(data);
        }
      } catch (err) {
        clearInterval(pollInterval);
        reject(err);
      }
    }, 2000); // Poll every 2 seconds

    // Timeout after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      reject(new Error('Job polling timeout'));
    }, 300000);
  });
}

function buildTinsFromJobResult(tinsData: any): any[] {
  const allMpinData: any[] = [];
  if (tinsData?.Data?.result) {
    const result = JSON.parse(tinsData.Data.result);
    for (const key in result) {
      if (Object.prototype.hasOwnProperty.call(result, key) && Array.isArray(result[key])) {
        result[key].forEach((tinItem: any) => {
          allMpinData.push({
            name: tinItem.TinName || tinItem.Tin,
            value: tinItem.Tin,
            disable: false,
          });
        });
      }
    }
  }
  allMpinData.sort((a, b) => String(a.value).localeCompare(String(b.value)));
  return allMpinData;
}

// ─── 5. Token Refresh ────────────────────────────────────────

/**
 * Mirrors Angular's SSOLoginService.getSsoTokenRefresh() / OHIDLoginService.getOHIDTokenRefresh()
 *
 * Angular: toggleNewLoginApiChanges=true → POST, false → GET
 * Interceptor adds same standard headers as callback.
 */
export async function refreshAccessToken(config: AuthConfig): Promise<string> {
  const baseUrl = config.isInternal ? config.ssoApiProxyUrl : config.ohidApiProxyUrl;
  const endpoint = config.refreshUrl;
  const method = config.toggleNewLoginApiChanges ? 'POST' : 'GET';

  // Build headers matching Angular's token-interceptor EXACTLY
  const loggedUser = getLoggedUser();
  const userId = config.isInternal ? loggedUser?.MsId || '' : loggedUser?.OptumId || '';

  const headers: Record<string, string> = {
    Accept: '*/*',
    'Content-Type': 'application/json',
    'x-idp': config.isInternal ? 'azuread' : 'onehealthcareid',
    'X-Upstream-Env': config.upstreamEnv,
    UserType: config.isInternal ? 'Internal' : 'External',
  };

  if (userId) {
    headers['UserId'] = userId;
  }

  // Attach Bearer token (interceptor attaches for refresh URL when currentUser exists)
  const currentToken = getAccessToken(config);
  if (currentToken) {
    headers['Authorization'] = `Bearer ${currentToken}`;
  }

  // XSRF for POST
  if (method === 'POST' && config.XSRFToggle) {
    const xsrfToken = getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      headers['X-XSRF-TOKEN'] = xsrfToken;
    }
  }

  // Angular interceptor: withCredentials only when XSRFToggle && POST && xsrf token
  const credentials: RequestCredentials = config.isInternal
    ? config.XSRFToggle && method === 'POST'
      ? 'include'
      : 'same-origin'
    : 'include';

  const response = await fetch(`${baseUrl}${endpoint}`, {
    method,
    credentials,
    headers,
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  if (config.isInternal) {
    // Internal: server sets new cookies via Set-Cookie header
    // Same multi-strategy as parseInternalResponse: try cookie header → document.cookie
    let newToken = '';
    const cookieHeader = response.headers.get('cookie') || '';
    if (cookieHeader) {
      const cookies = cookieHeader.split(';');
      const tokenCookies = cookies.filter(
        (c: string) => c.toLowerCase().includes('access') && c.toLowerCase().includes('token')
      );
      if (tokenCookies.length > 0) {
        const eqIdx = tokenCookies[0].indexOf('=');
        newToken =
          eqIdx > -1
            ? tokenCookies[0]
                .substring(eqIdx + 1)
                .trim()
                .replace(/^"|"$/g, '')
            : '';
      }
    }
    // Fallback: read from document.cookie (Set-Cookie already applied by browser)
    if (!newToken) {
      newToken = getCookie(`${config.cookieEnvironment}ACCESS_TOKEN`) || '';
    }

    if (newToken) {
      setCookie(`${config.cookieEnvironment}ACCESS_TOKEN`, newToken, config.domain);
    }
    return newToken;
  } else {
    // External: new tokens in response body
    const body = await response.json();
    const newToken = body?.access_token || body?.accessToken || '';
    const newRefresh = body?.refresh_token || body?.refreshToken || null;
    const newIdToken = body?.id_token || body?.idToken || null;
    storeTokens(newToken, newRefresh, newIdToken, config);

    // Update currentUser tokens in sessionStorage (mirrors Angular token interceptor update)
    const currentUser = getCurrentUser();
    if (currentUser && currentUser[0]) {
      currentUser[0].AccessToken = newToken;
      if (newRefresh) currentUser[0].RefreshToken = newRefresh;
      if (newIdToken) currentUser[0].IdToken = newIdToken;
      saveCurrentUser(currentUser, config);
    }

    return newToken;
  }
}

// ─── 6. Logout ───────────────────────────────────────────────

export async function logout(config: AuthConfig, logoutType: string = 'user'): Promise<void> {
  console.log('[React Auth] logout called with type:', logoutType);
  const idToken = getIdToken(config);
  const sessionCount = getSessionCount(config);

  if (!config.isInternal) {
    // Mirror Angular clearingLogoutSession: clear everything then restore post-logout flags
    localStorage.removeItem('currentUser');
    localStorage.removeItem('loggedUser');
    localStorage.removeItem('UHC_CURRENT_USER');
    localStorage.removeItem('UHC_ID_TOKEN');
    localStorage.removeItem('UHC_UUID');
    localStorage.removeItem('topFilterTin');
    localStorage.removeItem('mpinTinData');
    sessionStorage.clear();
    sessionStorage.setItem('logout', 'true');
    sessionStorage.setItem('logoutType', logoutType);
    stopAutoRefresh();

    if (idToken && config.logoutRedirectUrl) {
      window.location.href = config.logoutRedirectUrl.replace('id_token_hint_delimiter', idToken);
    } else if (config.providerLoginPage) {
      window.location.href = config.providerLoginPage;
    }
    return;
  }

  // Only call server revoke on last tab (mirrors Angular ssoTokenLogout logic)
  if (sessionCount <= 1) {
    // Mirrors Angular revokeSsoToken / revokeOHIDToken — uses getLoginURL()
    const baseUrl =
      config.XSRFToggle && config.postLoginProxyUrl
        ? config.postLoginProxyUrl
        : config.isInternal
        ? config.ssoApiProxyUrl
        : config.ohidApiProxyUrl;
    const endpoint = config.logoutUrl;

    const loggedUser = getLoggedUser();
    const userId = config.isInternal ? loggedUser?.MsId || '' : loggedUser?.OptumId || '';

    const headers: Record<string, string> = {
      Accept: '*/*',
      'Content-Type': 'application/json',
      'x-idp': config.isInternal ? 'azuread' : 'onehealthcareid',
      'X-Upstream-Env': config.upstreamEnv,
      UserType: config.isInternal ? 'Internal' : 'External',
    };

    // UserId header
    if (userId) {
      headers['UserId'] = userId;
    }

    // Angular interceptor SKIPS Authorization for URLs containing 'logout'
    // So NO Authorization header for logout

    // XSRF for POST
    if (config.XSRFToggle) {
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = xsrfToken;
      }
    }

    try {
      await fetch(`${baseUrl}${endpoint}`, {
        method: 'POST',
        credentials: config.XSRFToggle ? 'include' : 'same-origin',
        headers,
      });
    } catch {
      // Best-effort server logout
    }
  } else {
    decrementSessionCount(config);
  }

  // Clear auth cookies (internal only — external uses sessionStorage only)
  if (config.isInternal) {
    deleteCookie(`${config.cookieEnvironment}ACCESS_TOKEN`, config.domain);
    deleteCookie(`${config.cookieEnvironment}REFRESH_TOKEN`, config.domain);
    deleteCookie(`${config.cookieEnvironment}ID_TOKEN`, config.domain);
    deleteCookie(`${config.cookieEnvironment}SESSION_COUNT`, config.domain);
  }

  // Clear localStorage — mirrors Angular clearingLogoutSession which removes currentUser,
  // loggedUser, UHC_CURRENT_USER, plus any keys React writes during a session.
  localStorage.removeItem('currentUser');
  localStorage.removeItem('loggedUser');
  localStorage.removeItem('UHC_CURRENT_USER');
  localStorage.removeItem('UHC_ID_TOKEN');
  localStorage.removeItem('UHC_UUID');
  localStorage.removeItem('topFilterTin');
  localStorage.removeItem('mpinTinData');
  // Persist logout flag for next page load (mirrors Angular localStorage.isLoggedOut)
  localStorage.setItem('isLoggedOut', 'true');

  // Mirror Angular clearingLogoutSession: wipe full sessionStorage then restore only the two
  // post-logout keys that Angular itself re-sets after sessionStorage.clear().
  sessionStorage.clear();
  sessionStorage.setItem('logout', 'true');
  sessionStorage.setItem('logoutType', logoutType);

  stopAutoRefresh();
}

// ─── 7. Auth Check (mirrors auth.guard.ts) ──────────────────

export function isAuthenticated(config: AuthConfig): boolean {
  const token = getAccessToken(config);
  if (!token) return false;

  if (config.isInternal) {
    return !!getCurrentUser();
  } else {
    const userData = getCurrentUser();
    if (!userData) return false;
    const user = Array.isArray(userData) ? userData[0] : userData;
    return !!(user?.Providers || user?.OptumId || user?.HealthCareOrganizationName);
  }
}

// ─── 8. Auto-Refresh Timer ──────────────────────────────────

let refreshTimer: ReturnType<typeof setInterval> | null = null;

export function startAutoRefresh(config: AuthConfig): void {
  const REFRESH_CHECK_MS = config.isInternal ? 120000 : 1500000; // 2 min for internal, 25 min for external

  stopAutoRefresh();
  refreshTimer = setInterval(async () => {
    try {
      const token = getAccessToken(config);
      if (!token) {
        stopAutoRefresh();
        return;
      }

      // For internal: check if token expires within 5 minutes (mirrors Angular hitRefreshTokenCall)
      if (config.isInternal) {
        const expiry = getTokenExpiry(token);
        if (expiry && expiry.getTime() - Date.now() > 5 * 60 * 1000) return;
      }

      await refreshAccessToken(config);
    } catch {
      stopAutoRefresh();
      await logout(config);
      if (config.isInternal) {
        window.location.replace('/');
      }
    }
  }, REFRESH_CHECK_MS);
}

export function stopAutoRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

// ─── 9. beforeUnload Handler (mirrors Angular app.component.ts) ──

let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

export function registerBeforeUnload(config: AuthConfig): void {
  unregisterBeforeUnload();

  beforeUnloadHandler = () => {
    if (sessionStorage.getItem('logout') === 'false') {
      const sessionCount = getSessionCount(config);
      if (sessionCount > 1) {
        decrementSessionCount(config);
      }

      // Internal: mark as reload for session restore
      if (config.isInternal) {
        sessionStorage.setItem('reload', 'true');
        sessionStorage.setItem('sessionCount', String(sessionCount));
      }
    }
  };
  window.addEventListener('beforeunload', beforeUnloadHandler);
}

export function unregisterBeforeUnload(): void {
  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
}

// ─── 10. OIDC Error Handling (mirrors Angular loggedInStateFlow) ──

function handleOidcErrors(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('error');

  const redirectErrors = new Set(['login_required', 'interaction_required', 'consent_required']);
  if (error && redirectErrors.has(error)) {
    // Mirrors Angular: redirect to SsoRedirectUriWithOutPrompt
    // This will be handled by Angular route since we share the same URL space
    return true;
  }
  return false;
}

// ─── 11. Gold Card Banner (mirrors Angular query param extraction) ──

function extractGoldCardBanner(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const goldCardBanner = urlParams.get('q');
  if (goldCardBanner) {
    sessionStorage.setItem('goldCardBanner', goldCardBanner);
  }
}

// ─── 12. Complete Login Flow ─────────────────────────────────

/**
 * Full login flow entry point for React — mirrors Angular LoginStubComponent.ngOnInit exactly.
 *
 * 1. Handle OIDC errors in URL
 * 2. Extract gold card banner param
 * 3. Check existing session (cookies/sessionStorage)
 * 4. If auth code in URL → exchange for tokens → fetch user details
 * 5. If no session → redirect to IdP
 * 6. For external: fetch V3 provider data → build Tins → save currentUser
 * 7. Start auto-refresh + beforeUnload handler
 */
export async function handleLoginFlow(config: AuthConfig): Promise<{
  token: string;
  user: any;
} | null> {
  console.log('[React Auth] ========== handleLoginFlow START ==========');
  console.log('[React Auth] config.isInternal:', config.isInternal);
  console.log('[React Auth] config.ssoApiProxyUrl:', config.ssoApiProxyUrl);
  console.log('[React Auth] config.callbackUrl:', config.callbackUrl);
  console.log('[React Auth] config.fetchUserDetailsUrl:', config.fetchUserDetailsUrl);
  console.log('[React Auth] window.location.href:', window.location.href);

  // Handle OIDC errors
  if (handleOidcErrors()) {
    console.log('[React Auth] OIDC error detected — returning null');
    return null;
  }

  // Extract gold card param
  extractGoldCardBanner();

  // A persisted marker is stale after a browser relaunch; current-session logout state stays in sessionStorage.
  if (config.isInternal && localStorage.getItem('isLoggedOut') === 'true') {
    localStorage.removeItem('isLoggedOut');
  }
  if (!sessionStorage.getItem('logout')) {
    sessionStorage.setItem('logout', 'false');
  }

  const isJustLoggedOut = sessionStorage.getItem('logout') === 'true';

  // ─── Check for auth code in URL FIRST ─────────────────────
  // Angular: (this.loggedInState && !isJustLoggedOut) || hasAuthCode → loggedInStateFlow
  // If auth code is present, Angular ALWAYS processes it (even with existing tokens)
  const urlParams = new URLSearchParams(window.location.search);
  // ─── Callback-flow gate ───────────────────────────────────
  // OHID_LOGIN_INITIATED is written exclusively by initiateLogin() — it is never derived
  // from URL parameters. Using this session flag as the branch condition ensures the
  // security decision (callback path vs. existing-session path) is NOT influenced by
  // user-controlled data, satisfying CWE-807 / CodeQL js/user-controlled-bypass.
  const isLoginInitiated = sessionStorage.getItem('OHID_LOGIN_INITIATED') === 'true';
  if (isLoginInitiated) {
    sessionStorage.removeItem('OHID_LOGIN_INITIATED');
  }
  // isCallbackFlow is derived solely from server-set session state — never from URL params.
  const isCallbackFlow = isLoginInitiated;

  // Validate the raw auth code from the URL. This value is user-controlled and is
  // intentionally NOT used to decide which flow branch to take — only isCallbackFlow
  // (a session value) controls that. The validated code is used only as API call data.
  const rawAuthCode = urlParams.get('code');
  const AUTH_CODE_PATTERN = /^[A-Za-z0-9\-._~]+$/;
  const isValidAuthCode =
    typeof rawAuthCode === 'string' &&
    rawAuthCode.length >= 10 &&
    rawAuthCode.length <= 2048 &&
    AUTH_CODE_PATTERN.test(rawAuthCode);

  if (rawAuthCode && !isValidAuthCode) {
    console.warn(
      '[React Auth] Ignoring malformed auth code from URL — does not match expected OAuth code format.'
    );
  }
  console.log('[React Auth] isCallbackFlow:', isCallbackFlow);
  console.log('[React Auth] isJustLoggedOut:', isJustLoggedOut);
  // ─── Check existing valid session (only if NOT a callback flow) ──
  if (!isCallbackFlow) {
    const existingToken = getAccessToken(config);
    const existingUser = getCurrentUser();
    if (existingToken && existingUser && !getSessionUser()) {
      saveCurrentUser(existingUser, config);
      const restoredLoggedUser = getLoggedUser();
      if (restoredLoggedUser) saveLoggedUser(restoredLoggedUser);
      window.location.replace('/myhome');
      return null;
    }
    if (existingToken && existingUser) {
      const userData = Array.isArray(existingUser) ? existingUser[0] : existingUser;
      if (config.isInternal && !isTokenExpired(existingToken)) {
        // Restore sessionStorage from localStorage so parcels (NavigationBar etc.)
        // that read sessionStorage directly still work on new tabs.
        saveCurrentUser(existingUser, config);
        const restoredLoggedUser = getLoggedUser();
        if (restoredLoggedUser) saveLoggedUser(restoredLoggedUser);
        startAutoRefresh(config);
        registerBeforeUnload(config);
        return { token: existingToken, user: existingUser };
      }
      if (!config.isInternal && userData?.AccessToken) {
        saveCurrentUser(existingUser, config);
        const restoredLoggedUser = getLoggedUser();
        if (restoredLoggedUser) saveLoggedUser(restoredLoggedUser);
        startAutoRefresh(config);
        registerBeforeUnload(config);
        return { token: existingToken, user: existingUser };
      }
    }

    // ─── Internal: validate existing token on load ────────────
    if (config.isInternal && existingToken) {
      if (isTokenExpired(existingToken)) {
        deleteCookie(`${config.cookieEnvironment}ACCESS_TOKEN`, config.domain);
        deleteCookie(`${config.cookieEnvironment}REFRESH_TOKEN`, config.domain);
        deleteCookie(`${config.cookieEnvironment}SESSION_COUNT`, config.domain);
        sessionStorage.clear();
        window.location.reload();
        return null;
      }
      const sessionCount = getSessionCount(config);
      if (isNaN(sessionCount) || sessionCount === -1) {
        deleteCookie(`${config.cookieEnvironment}ACCESS_TOKEN`, config.domain);
        deleteCookie(`${config.cookieEnvironment}SESSION_COUNT`, config.domain);
        sessionStorage.clear();
        window.location.reload();
        return null;
      }
    }

    // No code, no session → redirect to login
    if (!isJustLoggedOut) {
      await initiateLogin(config);
    }
    return null;
  }

  // ─── Callback flow: validate the auth code before use as data ─
  // isCallbackFlow is already confirmed true above. Reject early if the URL code is
  // absent or malformed — authCode is used only as an API parameter, never as a gate.
  if (!isValidAuthCode || !rawAuthCode) {
    console.warn('[React Auth] Callback flow initiated but no valid auth code in URL — aborting');
    return null;
  }
  const authCode: string = rawAuthCode; // narrowed to string; taint stays out of branch decisions

  console.log('[React Auth] authCode:', `${authCode.substring(0, 15)}... (present)`);

  // Clear logout flag now that we are processing a fresh callback
  if (isJustLoggedOut) {
    sessionStorage.setItem('logout', 'false');
  }

  // ─── Exchange auth code for tokens ────────────────────────
  console.log('[React Auth] Exchanging auth code:', authCode.substring(0, 10) + '...');
  let tokenResult: TokenExchangeResult;
  try {
    tokenResult = await exchangeAuthCode(authCode, config);
    console.log(
      '[React Auth] exchangeAuthCode result — accessToken:',
      tokenResult.accessToken
        ? `${tokenResult.accessToken.substring(0, 20)}... (${tokenResult.accessToken.length})`
        : 'EMPTY',
      'idToken:',
      tokenResult.idToken
        ? `${tokenResult.idToken.substring(0, 20)}... (${tokenResult.idToken.length})`
        : 'NULL'
    );
  } catch (err: any) {
    console.error('[React Auth] exchangeAuthCode FAILED:', err?.status, err?.message);
    // Mirrors Angular ssoLogIn error handler: on any callback failure → cookieSession logout
    await logout(config, 'cookieSession');
    return null;
  }

  if (!tokenResult.accessToken) {
    console.error('[React Auth] No accessToken after exchange — triggering cookieSession logout');
    // Mirrors Angular ssoLogIn error handler: no token → cookieSession logout
    await logout(config, 'cookieSession');
    return null;
  }

  // Store tokens
  console.log('[React Auth] Storing tokens...');
  storeTokens(tokenResult.accessToken, tokenResult.refreshToken, tokenResult.idToken, config);

  let user: any;

  if (config.isInternal) {
    // ─── INTERNAL SSO FLOW ──────────────────────────────────
    console.log('[React Auth] Internal SSO flow — extracting MSAD groups...');

    // Extract MSAD groups from ID token (mirrors Angular getMsadGroups)
    extractAndSaveMsadGroups(tokenResult.idToken);

    // Fetch user details — Angular calls fetchUserDetailCall unconditionally (no null check on idToken)
    // The idToken is passed as query param; if null, server still gets the request
    console.log(
      '[React Auth] About to call fetchUserDetails — idToken:',
      tokenResult.idToken ? 'present' : 'NULL/EMPTY'
    );
    try {
      console.log('[React Auth] Calling fetchUserDetails...');
      user = await fetchUserDetails(tokenResult.idToken || getIdToken(config) || '', config);
      console.log(
        '[React Auth] fetchUserDetails returned:',
        user ? 'user data received' : 'null/undefined'
      );
    } catch (err: any) {
      // Mirrors Angular fetchUserDetailCall error handler:
      // 500 → reRoutingOfPageDuringSsoPageRefresh (clear session, reload)
      // Other → ssoTokenLogout('cookieSession')
      if (err?.status === 500) {
        deleteCookie(`${config.cookieEnvironment}ACCESS_TOKEN`, config.domain);
        deleteCookie(`${config.cookieEnvironment}SESSION_COUNT`, config.domain);
        sessionStorage.clear();
        window.location.reload();
        return null;
      }
      await logout(config, 'cookieSession');
      return null;
    }
    if (!user?.UserPersonas?.length) {
      // Mirrors Angular checkUserAccess: no personas → logout as inactive
      await logout(config, 'inactive');
      return null;
    }

    // Save user data
    if (user) {
      saveCurrentUser([{ ...user }], config);
      saveLoggedUser(user);

      // Encrypt and save MsId (mirrors Angular encryptMsidService)
      encryptAndSaveMsId(user.MsId);

      // Set SESSION_COUNT (mirrors Angular navigateToLandingPage)
      incrementSessionCount(config);
    }
  } else {
    // ─── EXTERNAL OHID FLOW ─────────────────────────────────

    // Store UUID/emulatedUuid (mirrors Angular login-stub ohidLogIn)
    if (tokenResult.uuid) saveUuid(tokenResult.uuid);
    if (
      tokenResult.emulatedUuid !== null &&
      tokenResult.emulatedUuid !== undefined &&
      String(tokenResult.emulatedUuid) !== 'null'
    ) {
      sessionStorage.setItem('emulatedUuid', JSON.stringify(tokenResult.emulatedUuid));
    } else {
      sessionStorage.setItem('emulatedUuid', 'null');
    }

    // Save initial logged user from callback (mirrors Angular login-stub ohidLogIn)
    if (tokenResult.rawUser) {
      const ru = tokenResult.rawUser;
      if (ru.FirstName || ru.LastName || ru.EmailId || ru.OptumId) {
        saveLoggedUser(ru);
      }
    }

    // Save initial currentUser for AuthGuard (mirrors Angular login-stub ohidLogIn)
    const initialCurrentUser = {
      FirstName: tokenResult.rawUser?.FirstName || '',
      LastName: tokenResult.rawUser?.LastName || '',
      EmailId: tokenResult.rawUser?.EmailId || '',
      OptumId: tokenResult.rawUser?.OptumId || '',
      uuid: tokenResult.uuid,
      emulatedUUID: tokenResult.emulatedUuid,
      AccessToken: tokenResult.accessToken,
      RefreshToken: tokenResult.refreshToken || '',
      IdToken: tokenResult.idToken || '',
    };
    saveCurrentUser([initialCurrentUser], config);

    // Save idToken + uuid to localStorage for page refresh recovery
    if (tokenResult.idToken) localStorage.setItem('UHC_ID_TOKEN', tokenResult.idToken);
    if (tokenResult.uuid) localStorage.setItem('UHC_UUID', tokenResult.uuid);

    // Fetch V3 provider data (mirrors Angular external.service.CheckExternalOHID)
    const v3Response = await fetchExternalProviderData(
      tokenResult.accessToken,
      tokenResult.uuid,
      tokenResult.emulatedUuid,
      tokenResult.idToken,
      config
    );

    if (!v3Response || v3Response.StatusCode !== 200 || !v3Response.Data?.length) {
      throw new Error(v3Response?.Message || 'Provider data is empty or invalid');
    }

    // Transform V3 response
    const transformed = transformV3Response(
      v3Response,
      tokenResult.accessToken,
      tokenResult.refreshToken,
      tokenResult.idToken,
      tokenResult.uuid,
      config
    );

    let finalTins = transformed.tinsArray;
    let finalSelectedTin = transformed.selectedTin;
    let finalProviders = transformed.providers;

    // TIN level job submission if enabled (mirrors Angular external.service)
    if (config.tinLevelExternalChange && transformed.dataArray.length > 0) {
      try {
        const jobData = await submitTinJob(
          transformed.dataArray,
          tokenResult.uuid,
          tokenResult.accessToken,
          config
        );
        if (jobData?.Data?.JobId) {
          const tinsData = await pollTinJobResult(
            jobData.Data.JobId,
            tokenResult.accessToken,
            config
          );
          const jobTins = buildTinsFromJobResult(tinsData);
          if (jobTins.length > 0) {
            finalTins = jobTins;
            finalSelectedTin = jobTins[0];
            // Build Providers array with ALL object (mirrors Angular TIN-level path)
            const result = JSON.parse(tinsData.Data.result);
            finalProviders = [{ ALL: result }];
          }
        }
      } catch (err) {
        // TIN job failed — fall back to non-TIN-level Tins
        console.warn('[React Auth] TIN job failed, using provider-based Tins:', err);
      }
    }

    // Build final currentUser with all data
    const currentUserFinal = {
      ...transformed.currentUserData,
      Tins: finalTins,
      selectedTin: finalSelectedTin,
      Providers: finalProviders,
    };

    saveCurrentUser([currentUserFinal], config);

    // Angular keeps loggedUser identity from the ID token/login callback even when
    // the V3 provider response has blank FirstName/LastName/EmailId/OptumId fields.
    const loggedUserFinal = {
      FirstName: tokenResult.rawUser?.FirstName || transformed.loggedUserData.FirstName || '',
      LastName: tokenResult.rawUser?.LastName || transformed.loggedUserData.LastName || '',
      EmailId: tokenResult.rawUser?.EmailId || transformed.loggedUserData.EmailId || '',
      OptumId: tokenResult.rawUser?.OptumId || transformed.loggedUserData.OptumId || '',
      MsId: null,
      EmulatedUserDetails: transformed.emulatedUserDetails || undefined,
    };
    saveLoggedUser(loggedUserFinal);
    setMpinTinDetails(finalTins);

    // Set external session defaults (mirrors Angular external.service)
    setExternalSessionDefaults();
    sessionStorage.setItem('multiTinNavigation', config.tinLevelExternalChange ? 'true' : 'false');
    hydrateExternalSessionState(finalTins, finalSelectedTin);

    // Store topFilterTin for filter components
    if (finalSelectedTin) {
      sessionStorage.setItem('topFilterTin', JSON.stringify(finalSelectedTin));
    }

    // Set OptumId (mirrors Angular login-stub navigateToLandingPageExternal)
    if (loggedUserFinal.OptumId) {
      sessionStorage.setItem('OptumId', loggedUserFinal.OptumId);
    }

    user = currentUserFinal;
  }

  // ─── Common post-login ────────────────────────────────────
  console.log('[React Auth] ========== LOGIN FLOW COMPLETE ==========');
  console.log(
    '[React Auth] user:',
    user ? 'YES' : 'NO',
    'token length:',
    tokenResult.accessToken?.length
  );
  sessionStorage.setItem('logout', 'false');

  startAutoRefresh(config);
  registerBeforeUnload(config);

  return { token: tokenResult.accessToken, user };
}
